import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import { useDebounce } from '../../../hooks/useDebounce'
import {
  AdditionalInvoicePreview,
  BalanceLineKind,
  previewAdditionalInvoice,
} from '../../../services/teamBalance'

/** Long enough to skip the amounts a rep passes through, short enough to feel live. */
const AMOUNT_DEBOUNCE_MS = 300

interface UseAdditionalInvoicePreviewOptions {
  teamId: string
  /** Roster-scoped (D40), so a preview from one roster is never served for another. */
  rosterId: string
  /** Null while the modal is closed. */
  slotId: string | null
  /** The amount being added, in cents; nothing is fetched until it is positive. */
  amountCents: number
  /** Sep 23 — the kind of the row; an official line's preview also returns its player effect. */
  expectedKind?: BalanceLineKind
}

/**
 * SN-8310 — the EXACT server preview of the charge Add Invoice would create, while
 * the modal is open. Modeled on useLockPreview: no staleness tolerance (money may
 * move between opens), no retries (a 422 is the answer, not a glitch).
 *
 * The amount is debounced, and `settling` is true until the preview on screen is for
 * the amount the rep typed — the confirm stays disabled until then, so a fingerprint
 * is only ever submitted with the amount it was computed for.
 */
export const useAdditionalInvoicePreview = ({
  teamId,
  rosterId,
  slotId,
  amountCents,
  expectedKind,
}: UseAdditionalInvoicePreviewOptions) => {
  const debouncedCents = useDebounce(amountCents, AMOUNT_DEBOUNCE_MS)

  const query = useQuery<AdditionalInvoicePreview>({
    queryKey: queryKeys.teamBalance.additionalInvoicePreview(
      teamId,
      rosterId,
      slotId ?? '',
      debouncedCents
    ),
    queryFn: () =>
      previewAdditionalInvoice(
        teamId,
        rosterId,
        slotId as string,
        debouncedCents,
        expectedKind
      ),
    enabled:
      Boolean(teamId) &&
      Boolean(rosterId) &&
      slotId !== null &&
      debouncedCents > 0,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  return {
    ...query,
    preview: query.data ?? null,
    /** The preview's amount, in cents — what a confirm must submit. */
    previewCents: debouncedCents,
    settling: debouncedCents !== amountCents,
  }
}

export default useAdditionalInvoicePreview
